import _ from "lodash"
import { ReactNode } from "react"
import { ThemeOptions } from "@mui/material"

// The menu configuration utilized in the app's service configuration.
// eslint-disable-next-line @typescript-eslint/no-unused-vars

interface RouteConfig {
  path: string
  element: ReactNode
}

type MergeRoutes = (
  sourceRoutes: RouteConfig[],
  extensionRoutes: RouteConfig[]
) => RouteConfig[]

type ThemeOptionFactory = (theme: ThemeOptions) => ThemeOptions

interface ThemeOptionFactories {
  mobile?: ThemeOptionFactory
  "kiosk-h"?: ThemeOptionFactory
  "kiosk-v"?: ThemeOptionFactory
}

interface ExtensionConfig {
  themeOptionFactories: ThemeOptionFactories
  ignoreAppThemeFactory?: boolean
  routes: RouteConfig[]
}

type GetAllExtensionRoutes = (
  extensionList: string[],
  extensionConfigs: ExtensionConfig[] | object
) => RouteConfig[]

export const getAllExtensionRoutes: GetAllExtensionRoutes = (
  extensionList = [],
  extensionConfigs = {},
  deviceConfig = "mobile"
) => {
  if (_.isEmpty(extensionList)) return []

  return _(extensionList)
    .map((extensionName) => {
      // Retrieve rotue config by extension name and the device mode. (kiosk-v, kiosk-h, mobile)
      const routes = _.get(extensionConfigs, [
        extensionName,
        "routes",
        deviceConfig,
      ])
      if (_.isNil(routes) || _.isEmpty(routes)) return null
      return routes
    })
    .compact()
    .flatten()
    .value()
}

export const getExtensionThemeFactories = (
  extensionList = [],
  extensionConfigs = {},
  deviceConfig = "mobile"
) => {
  const result = { themeFactories: [], ignoreAppThemeFactory: false }

  if (_.isEmpty(extensionList)) return result

  let ignoreAppThemeFactory = false

  const themeFactories = _(extensionList)
    .map((extensionName) => {
      const {
        themeOptionFactories,
        ignoreAppThemeFactory: shouldIgnoreAppThemeFactory,
      } = _.get(extensionConfigs, extensionName, {
        ignoreAppThemeFactory: false,
        themeOptionFactories: {},
      })
      const themeOptionFactory = _.get(themeOptionFactories, deviceConfig)
      if (shouldIgnoreAppThemeFactory)
        ignoreAppThemeFactory = shouldIgnoreAppThemeFactory

      if (_.isNil(themeOptionFactory) || !_.isFunction(themeOptionFactory))
        return null
      return themeOptionFactory
    })
    .compact()
    .value()

  return { themeFactories, ignoreAppThemeFactory }
}

export const mergeRoutes: MergeRoutes = (
  sourceRoutes = [],
  extensionRoutes = []
) => {
  const mergedRouteConfigs = _.merge(
    _.keyBy(sourceRoutes, "path"),
    _.keyBy(extensionRoutes, "path")
  )
  return _.values(mergedRouteConfigs)
}
